import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2 } from '../../utils/icons';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';

interface IEstrutura {
  id: number;
  nome: string;
  empresa_id: number;
  created_at: string;
}

const EstruturaOrganizacionalList: React.FC = () => {
  const [estruturas, setEstruturas] = useState<IEstrutura[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await api.getEstruturasOrganizacionais(empresaId ?? undefined);
        if (response.success && response.data) {
          setEstruturas(response.data as IEstrutura[]);
        } else {
          setError((response.error as string) || 'Erro ao carregar dados.');
        }
      } catch {
        setError('Erro de conexão.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [empresaId]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deseja excluir esta estrutura organizacional?')) return;
    try {
      const response = await api.deleteEstruturaOrganizacional(id);
      if (response.success) {
        setEstruturas(prev => prev.filter(e => e.id !== id));
      } else {
        setError((response.error as string) || 'Erro ao excluir.');
      }
    } catch {
      setError('Erro de conexão.');
    }
  };

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Estruturas Organizacionais</h1>
        <Button variant="primary" onClick={() => navigate('/app/estrutura-organizacional')}>
          <FiPlus /> Nova Estrutura
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="text-center p-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {estruturas.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-muted py-3">
                  Nenhuma estrutura organizacional cadastrada.
                </td>
              </tr>
            ) : (
              estruturas.map(e => (
                <tr key={e.id}>
                  <td>{e.id}</td>
                  <td>{e.nome}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/app/estrutura-organizacional/editar/${e.id}`)}
                      >
                        <FiEdit2 /> Editar
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(e.id)}
                      >
                        <FiTrash2 /> Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default EstruturaOrganizacionalList;
